// src/pages/Accounts/ExpenseList.js
import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Row, Col, Typography, Tag, App, Tooltip, Drawer, Input, Grid } from "antd";
import { ArrowLeftOutlined, EditOutlined, PlusOutlined,   QuestionCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchExpenses, deleteExpense } from "./expenseThunk";
import {
  resetExpenses,
  setSelectedExpense,
  setIsEditExpense,
  setRefreshList,
} from "./expenseSlice";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import { formatDateOnly, getAccessType } from "../../lib/functions";
import { DeleteOutlined } from "@ant-design/icons";
import { useIsAdminUser } from "../../lib/accessList";
import { useTxLock } from "../../hooks/useTxLock";

const format = commaNumber.bindWith(",", ".");

const { Title } = Typography;

const ExpenseList = () => {
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { list, loading, refreshList } = useSelector((state) => state.expenses);
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const isAdminUser = useIsAdminUser();
  const authInfo = useSelector((state) => state.auth?.auth_info);
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  useEffect(() => {
    const access = getAccessType("expense_entry");
    access_type_set(access);
  }, []);

  useEffect(() => {
    dispatch(resetExpenses());
    dispatch(fetchExpenses());
  }, [dispatch]);

  useEffect(() => {
    if (refreshList) {
      dispatch(fetchExpenses());
      dispatch(setRefreshList(false));
    }
  }, [refreshList, dispatch]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10, 
  });
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();

  const filteredExpenses = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return list;

    return list.filter((e) =>
      [
        ...Object.values(e || {}).filter((v) => v !== null && typeof v !== "object"),
        ...(e?.details || []).map((d) => d.acc_name),
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [list, searchText]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleDelete = async (exp_id) => {
    try {
      await dispatch(deleteExpense(exp_id)).unwrap();
      modal.success({
        title: "Expense entry deleted successfully",
      });
      dispatch(fetchExpenses());
    } catch (error) {
      modal.warning({
        title: error.message || "Delete failed",
      });
    }
  };

  const columns = [
    {
      title: "No.",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Expense Code",
      dataIndex: "exp_code",
    },
    {
      title: "Expense Date",
      dataIndex: "created_date",
      render: (value) => (value ? formatDateOnly(value) : "-"),
    },
    {
      title: "Pay From Account",
      dataIndex: "acc_name",
      render: (val, record) => val || record.from_acc_name || "",
    },
   {
  title: "Pay To",
  dataIndex: "pay_to",
  render: (val, record) => {
    if (!record.recog_id) {
      const heads = (record.details || [])
        .map((d) => d.acc_name)
        .filter(Boolean)
        .join(", ");
      return heads || val || "-";
    }
    return val || "-";
  },
},
    {
      title: "Total",
      dataIndex: "exp_total",
      align: "right",
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      title: "Description",
      dataIndex: "narration",
    },
    {
      title: "Created By",
      dataIndex: "full_name",
    },
 {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.created_date);

    return (
      <>
        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            disabled={!(isAdminUser || canEdit) || txLocked}
            icon={<EditOutlined />}
            onClick={() => {
              if (txLocked) return;

              dispatch(setSelectedExpense(record));
              dispatch(setIsEditExpense(true));

              navigate(`/accounts/expenses/edit/${record.exp_id}`);
            }}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Button
            type="link"
            danger
            disabled={!(isAdminUser || canDelete) || txLocked}
            icon={<DeleteOutlined />}
            onClick={() => {
              if (txLocked) return;

              modal.confirm({
                title: "Are you sure?",
                content: `This will delete expense entry with amount: ${record.exp_total}`,
                okText: "Yes, Delete",
                cancelText: "Cancel",
                onOk: () => handleDelete(record.exp_id),
              });
            }}
          />
        </Tooltip>
      </>
    );
  },
}
  ];

  const detailColumns = [
    { title: "Sl No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Expense Head", dataIndex: "acc_name" },
    {
      title: "Amount",
      dataIndex: "exp_amount",
      align: "right",
      render: (val) => format(Number(val || 0).toFixed(2)),
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
          onClick={() => navigate(-1)}
          data-testid="expense-list-back-button"
        />
        <Title level={3} style={{ margin: 0 }}>
          Expense Entry List
        </Title>

        <div style={{ marginLeft: "auto" }}>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </div>
      </div>
      <Row
        justify="space-between"
        align="middle"
        style={{
          margin: "32px 0",
          gap: !screens.md && "12px",
          flexWrap: !screens.md && "nowrap",
        }}
      >
        <Col>
          <Input
            placeholder="Search expenses..."
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: screens.md ? 250 : 200 }}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPagination((p) => ({ ...p, current: 1 }));
            }}
          />
        </Col>

        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  dispatch(setSelectedExpense(null));
                  dispatch(setIsEditExpense(false));
                  navigate("/accounts/expenses/add");
                }}
                data-testid="btn-add-expense"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        bordered
        loading={loading}
        columns={columns}
        dataSource={filteredExpenses}
        rowKey="exp_id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        size="middle"
        scroll={{ x: "max-content" }}
        expandable={{
          expandedRowRender: (record) => {
            return (
              <Table
                rowKey="detailId"
                columns={detailColumns}
                dataSource={record?.details || []}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: (record?.details || []).length,
                  showQuickJumper: true,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  onChange: (page) => {
                    setCurrentPage(page);
                  },
                  onShowSizeChange: (_, size) => {
                    setPageSize(size);
                    setCurrentPage(1);
                  },
                }}
                size="small"
                bordered
              />
            );
          },
        }}
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "#fff",
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
    <Drawer
      title="Expense Entry Help"
      open={helpOpen}
      onClose={() => setHelpOpen(false)}
      width={430}
      styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
    >
      <div style={{ lineHeight: 1.8 }}>
        <h2
          style={{
            fontSize: "18px",
            fontWeight: 600,
            marginBottom: "12px",
            marginLeft: "8px",
          }}
        >
          Expense Entry Management
        </h2>

        <p
          style={{
            color: "#555",
            marginBottom: "16px",
            marginLeft: "8px",
          }}
        >
          The Expense Entry module is used to record and manage company expenses
          within the ERP accounting system. It helps track payments, expense heads,
          account movements, and financial reporting activities.
        </p>

        <div
          style={{
            background: "#f5f5f5",
            padding: "14px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <strong>Available Actions</strong>

          <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
            <li>View expense transaction records</li>
            <li>Use <strong>Add New</strong> to create expense entries</li>
            <li>Edit expense details when required</li>
            <li>Delete expense entries if permitted</li>
            <li>Track payment accounts and expense heads</li>
            <li>Monitor expense totals and narration details</li>
            <li>Review detailed expense breakdowns using expandable rows</li>
          </ol>
        </div>

        <div
          style={{
            background: "#fafafa",
            border: "1px solid #eee",
            padding: "14px",
            borderRadius: "8px",
          }}
        >
          <strong>Important Notes</strong>

          <ul
            style={{
              paddingLeft: "18px",
              marginTop: "10px",
              color: "#666",
            }}
          >
            <li>Expense entries affect accounting balances immediately.</li>
            <li>Expense heads help organize financial reporting.</li>
            <li>Expandable rows display detailed expense allocations.</li>
            <li>Access permissions control edit and delete actions.</li>
            <li>Transaction history is maintained for auditing and reports.</li>
          </ul>
        </div>
      </div>
    </Drawer>
    </>
  );
};

export default ExpenseList;
